import { Button, NativePdfPreview } from '@tempots/beatui'
import { attr, Ensure, html, Unless, Value } from '@tempots/dom'
import { BigLoader } from './big-loader'

export type PreviewBlob = {
  blob: Value<Blob | null>
}

export type PreviewUrl = {
  url: Value<string>
}

export type PreviewOptions = {
  noDownload?: Value<boolean>
  autodownload?: Value<boolean>
  filename: Value<string>
  suffix?: Value<string>
  onDownload?: () => void
}

export type PreviewResumeOptions = PreviewOptions & (PreviewBlob | PreviewUrl)

function isBlobOption(
  opt: PreviewResumeOptions
): opt is PreviewBlob & PreviewOptions {
  return 'blob' in opt
}

function getBlob(options: PreviewResumeOptions) {
  if (isBlobOption(options)) {
    return Value.toSignal(options.blob)
  }
  return Value.toSignal(options.url).mapAsync(async url => {
    const response = await fetch(url)
    const blob = await response.blob()
    return blob
  }, null)
}

async function downloadPdfResumeFromUrl(url: string, filename: string) {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Download failed: ${res.status}`)
  const blob = await res.blob()
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}

export function downloadPdfResumeFromBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  downloadPdfResumeFromUrl(url, filename)
  URL.revokeObjectURL(url)
}

function normalizeName(filename: string, suffix?: string) {
  if (suffix == null) return filename
  const parts = filename.split('.')
  parts.pop()
  const name = parts.join('.')
  return `${name}${suffix}.${'pdf'}`
}

export function PDFPreview(options: PreviewResumeOptions) {
  const blob = getBlob(options)
  const {
    noDownload,
    autodownload = false,
    filename,
    suffix,
    onDownload,
  } = options
  return html.div(
    attr.class('h-full overflow-hidden bg-gray-100 flex flex-col'),
    html.div(
      attr.class('flex-grow overflow-hidden'),
      Ensure(
        blob,
        blob => {
          blob.on(b => {
            if (b == null) return
            if (Value.get(autodownload)) {
              downloadPdfResumeFromBlob(
                b,
                normalizeName(
                  Value.get(filename),
                  Value.get(suffix as Value<string>)
                )
              )
              // Auto-download is a real download — report it like a click.
              onDownload?.()
            }
          })
          return NativePdfPreview({
            content: blob,
            view: 'Fit',
            toolbar: false,
            pagemode: 'none',
          })
        },
        () => BigLoader()
      )
    ),
    Unless(noDownload ?? false, () =>
      html.div(
        Button(
          {
            fullWidth: true,
            roundedness: 'none',
            size: 'lg',
            disabled: blob.map(v => v == null),
            variant: 'filled',
            color: 'primary',
            onClick: () => {
              if (blob.value == null) return
              downloadPdfResumeFromBlob(
                blob.value,
                normalizeName(
                  Value.get(filename),
                  Value.get(suffix as Value<string>)
                )
              )
              onDownload?.()
            },
          },
          'Download PDF'
        )
      )
    )
  )
}
